/**
 * Wallet Intelligence — admin dashboard aggregating the consumer signal
 * stream. Anonymous by design — no consumer identifiers, only counts and
 * averages. Feeds every downstream Intelligence Service preview.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { TID } from "@/constants/testIds";
import {
  Radio, BookOpen, GraduationCap, ShieldAlert, Sparkles, Award, Languages, ShieldCheck, Package, RefreshCw,
} from "lucide-react";

export default function WalletIntelligencePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = () => {
    setLoading(true);
    api.get("/wallet/intelligence/summary").then(r => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  };
  useEffect(() => { refresh(); }, []);

  if (loading) return <div className="text-sm text-muted-foreground py-20 text-center">Loading intelligence…</div>;
  if (!data) return <div className="text-sm text-rose-700 py-20 text-center">Failed to load intelligence.</div>;

  return (
    <div data-testid="wallet-intel-page" className="space-y-6 animate-fade-in-up">
      <header className="flex items-end justify-between border-b border-border pb-4 gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-altara-teal font-semibold">Wallet Intelligence</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-altara-ink mt-1">Live signal stream from consumer wallets</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
            Anonymous, aggregated signals from every consumer wallet across every tenant. Feeds the Consumer Intelligence Profile and every downstream Intelligence Service.
          </p>
        </div>
        <button onClick={refresh} className="text-xs text-altara-teal hover:text-altara-deep inline-flex items-center gap-1" data-testid="intel-refresh">
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      </header>

      {/* KPI row */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi icon={Radio}     label="Signals ingested" value={data.total_signals.toLocaleString()} />
        <Kpi icon={Package}   label="Unique devices"   value={data.unique_devices.toLocaleString()} />
        <Kpi icon={Award}     label="Literacy strong+" value={((data.literacy_band_distribution?.strong || 0) + (data.literacy_band_distribution?.solid || 0)).toLocaleString()} />
        <Kpi icon={ShieldAlert} label="Scam checks · high risk" value={(data.scam_band_distribution?.high || 0).toLocaleString()} />
      </section>

      {/* Two-column grids */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel icon={BookOpen} title="Top glossary lookups">
          <TopList items={data.top_glossary_terms} emptyLabel="No glossary lookups yet." />
        </Panel>
        <Panel icon={Sparkles} title="Top knowledge gaps (from NAVI)">
          <TopList items={data.top_knowledge_gaps} emptyLabel="No AI-classified gaps yet." />
        </Panel>
        <Panel icon={GraduationCap} title="Most-opened lessons">
          <TopList items={data.top_lessons_opened} emptyLabel="No lesson opens yet." />
        </Panel>
        <Panel icon={ShieldAlert} title="Top scam red flags detected">
          <TopList items={data.top_scam_red_flags} emptyLabel="No scam submissions yet." />
        </Panel>
      </div>

      {/* Distributions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <DistributionPanel icon={Award} title="Financial literacy bands" dist={data.literacy_band_distribution} colours={{ strong: "emerald", solid: "teal", developing: "amber", low: "rose" }} />
        <DistributionPanel icon={ShieldAlert} title="Scam Shield risk bands" dist={data.scam_band_distribution} colours={{ low: "emerald", caution: "amber", high: "rose" }} />
        <DistributionPanel icon={Languages} title="Wallet languages" dist={data.language_distribution} colours={{}} />
        <DistributionPanel icon={ShieldCheck} title="Decision confidence at completion" dist={data.decision_confidence_distribution} colours={{ very_confident: "emerald", somewhat_confident: "teal", need_more_information: "amber", would_like_contact: "slate" }} />
      </div>

      {/* Per-product intelligence table */}
      <Panel icon={Package} title="Per-product intelligence">
        {data.per_product_intelligence.length === 0 ? (
          <div className="text-sm text-slate-400 italic p-4">No completed reviews with a product type yet.</div>
        ) : (
          <div className="border border-slate-200 rounded-sm overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-[0.15em] text-slate-500">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">Product type</th>
                  <th className="text-right px-4 py-2 font-medium">Reviews</th>
                  <th className="text-right px-4 py-2 font-medium">Avg understanding</th>
                  <th className="text-right px-4 py-2 font-medium">Avg confidence Δ</th>
                </tr>
              </thead>
              <tbody>
                {data.per_product_intelligence.map(p => (
                  <tr key={p.product_type} className="border-t border-slate-100">
                    <td className="px-4 py-2 font-medium text-slate-800">{(p.product_type || "").replace(/_/g, " ")}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{p.reviews}</td>
                    <td className="px-4 py-2 text-right tabular-nums">
                      <span className={p.avg_understanding >= 70 ? "text-emerald-700" : p.avg_understanding >= 40 ? "text-amber-700" : "text-rose-700"}>{p.avg_understanding}/100</span>
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">
                      <span className={p.avg_confidence_delta > 0 ? "text-emerald-700" : p.avg_confidence_delta < 0 ? "text-rose-700" : "text-slate-500"}>
                        {p.avg_confidence_delta > 0 ? "+" : ""}{p.avg_confidence_delta}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}

function Kpi({ icon: Icon, label, value }) {
  return (
    <div className="border border-slate-200 rounded-sm bg-white p-4">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold">
        <Icon className="w-3.5 h-3.5 text-altara-teal" /> {label}
      </div>
      <div className="font-display text-3xl font-semibold text-altara-ink mt-1 tabular-nums">{value}</div>
    </div>
  );
}

function Panel({ icon: Icon, title, children }) {
  return (
    <section className="border border-slate-200 rounded-sm bg-white p-5">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-altara-teal font-semibold mb-3">
        <Icon className="w-3.5 h-3.5" /> {title}
      </div>
      {children}
    </section>
  );
}

function TopList({ items, emptyLabel }) {
  if (!items || items.length === 0) return <div className="text-sm text-slate-400 italic">{emptyLabel}</div>;
  const max = Math.max(...items.map(i => i.count));
  return (
    <div className="space-y-2">
      {items.map(it => (
        <div key={String(it.key)} className="flex items-center gap-3">
          <div className="text-xs text-slate-700 truncate flex-1">{String(it.key).replace(/_/g, " ")}</div>
          <div className="flex-1 relative h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div className="absolute inset-y-0 left-0 bg-altara-teal" style={{ width: `${(it.count / max) * 100}%` }} />
          </div>
          <div className="text-[11px] font-mono tabular-nums text-slate-500 w-8 text-right">{it.count}</div>
        </div>
      ))}
    </div>
  );
}

function DistributionPanel({ icon, title, dist, colours }) {
  const entries = Object.entries(dist || {});
  const total = entries.reduce((s, [, v]) => s + v, 0) || 1;
  const toneClass = { emerald: "bg-emerald-500", teal: "bg-teal-500", amber: "bg-amber-500", rose: "bg-rose-500", slate: "bg-slate-500" };
  return (
    <Panel icon={icon} title={title}>
      {entries.length === 0 ? (
        <div className="text-sm text-slate-400 italic">No data yet.</div>
      ) : (
        <>
          <div className="flex h-3 rounded-full overflow-hidden bg-slate-100">
            {entries.map(([k, v]) => (
              <div key={k} className={toneClass[colours?.[k]] || "bg-altara-teal"} style={{ width: `${(v / total) * 100}%` }} title={`${k}: ${v}`} />
            ))}
          </div>
          <div className="mt-3 grid grid-cols-2 gap-1 text-[11px] text-slate-600">
            {entries.map(([k, v]) => (
              <div key={k} className="flex items-center gap-1.5">
                <span className={`w-2 h-2 rounded-full ${toneClass[colours?.[k]] || "bg-altara-teal"}`} />
                <span className="truncate">{k.replace(/_/g, " ")}</span>
                <span className="font-mono tabular-nums text-slate-500 ml-auto">{v}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </Panel>
  );
}
